import { useEffect, useState } from "react";
import { useAuth } from "../components/AuthContext.jsx";
import FrequentlyAndAsk from "../components/FrequentlyAndAsk.jsx";
import PriceCalculator from "../components/PriceCalculator.jsx";
import Explore from "./Explore.jsx";
import BuildForGrowth from "./BuildForGrowth.jsx";
import BecomePartner from "./BecomePartner.jsx";
import { useTranslation } from "react-i18next";

const Landing = () => {
  const { t } = useTranslation();
  const auth = useAuth();

  useEffect(() => {
    if (!auth.token || !auth.user || !auth.merchant) {
      localStorage.removeItem("authToken", "merchantUser", "user");
    }
  }, [auth.token, auth.user, auth.merchant]);

  const features = [
    {
      title: t("beVisible"),
      desc: t("beVisibleDesc"),
    },
    {
      title: t("sellSmarter"),
      desc: t("sellSmarterDesc"),
    },
    {
      title: t("rewardLoyalty"),
      desc: t("rewardLoyaltyDesc"),
    },
    {
      title: t("promoteEasily"),
      desc: t("promoteEasilyDesc"),
    },
    {
      title: t("growTogether"),
      desc: t("growTogetherDesc"),
    },
    {
      title: t("insightsThatMatter"),
      desc: t("insightsThatMatterDesc"),
    },
  ];

  const [showCalculator, setShowCalculator] = useState(false);

  const testimonials = [
    {
      name: t("janeDoe"),
      text: t("janeDoeTestimonial"),
      image: "https://randomuser.me/api/portraits/women/68.jpg",
    },
    {
      name: t("johnSmith"),
      text: t("johnSmithTestimonial"),
      image: "https://randomuser.me/api/portraits/men/65.jpg",
    },
  ];

  useEffect(() => {
    const handleClickOutside = (e) => {
      const calculator = document.getElementById("price-calculator-popup");
      const button = document.getElementById("calculator-button");

      if (
        calculator &&
        button &&
        !calculator.contains(e.target) &&
        !button.contains(e.target)
      ) {
        setShowCalculator(false);
      }
    };

    const handleEscape = (e) => {
      if (e.key === "Escape") {
        setShowCalculator(false);
      }
    };

    if (showCalculator) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("keydown", handleEscape);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [showCalculator]);

  return (
    <div className="font-sans text-gray-900 bg-white">
      {/* Hero Section */}
      <section
        id="hero"
        className="relative h-screen flex items-center overflow-hidden text-white py-24 px-4 text-center shadow-lg"
      >
        <div className="absolute top-0 left-0 w-full h-full z-0">
          <video autoPlay muted loop className="w-full h-full object-cover">
            <source
              src="https://videos.pexels.com/video-files/2909914/2909914-uhd_2732_1440_24fps.mp4"
              type="video/mp4"
            />
            {t("browserDoesNotSupportVideo")}
          </video>
          <div className="absolute inset-0 bg-black bg-opacity-50"></div>
        </div>

        <div className="relative z-10 max-w-4xl mx-auto">
          <h1 className="text-2xl bg-gray-300 w-1/3 mx-auto border rounded-md px-5 py-3 md:text-6xl font-bold mb-6 animate-fade-in font-playfair text-primary-color">
            Zayverse
          </h1>
          <h2 className="newsreader-font text-xl font-bold">
            {t("heroSubtitle")}
          </h2>
          <p className="md:text-lg mb-8 text-gray-300 text-start">
            {t("heroDescription")}
          </p>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-10 bg-primary-color">
        <div className="w-full px-10">
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-center mb-3 font-playfair text-[#E4D9FF]">
              {t("whyZayverse")}
            </h2>
            <p className="text-[#E4D9FF] text-start">
              {t("whyZayverseDescription")}
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-10">
            {features.map((f, i) => (
              <div
                key={i}
                className="bg-[#E4D9FF] p-6 rounded-md shadow-md text-center"
              >
                <h3 className="text-xl font-semibold mb-2 newsreader-font">
                  {f.title}
                </h3>
                <p className="text-primary-color">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* explore section */}
      <Explore />

      {/* tool for growth */}
      <BuildForGrowth />

      <BecomePartner />

      {/* category */}
      <div id="category" className="my-3 px-2 border py-2">
        <h2 className="text-xl text-center font-bold font-playfair">
          {t("categoryList")}
        </h2>
      </div>

      {/* training */}
      <div id="training" className="my-3 px-2 border py-2">
        <h2 className="text-xl text-center font-bold font-playfair">
          {t("ourTraining")}
        </h2>
      </div>

      {/* FAQ section */}
      <FrequentlyAndAsk />

      {/* Testimonials Section */}
      <section className="py-20 bg-white" id="testimonials">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-2xl font-bold text-center mb-12 font-playfair">
            {t("whatPeopleSay")}
          </h2>
          <div className="grid md:grid-cols-2 gap-10">
            {testimonials.map((t, i) => (
              <div
                key={i}
                className="bg-gray-50 p-6 rounded-xl shadow text-center"
              >
                <img
                  src={t.image}
                  alt={t.name}
                  className="w-16 h-16 rounded-full mx-auto mb-4"
                />
                <p className="italic text-gray-700 mb-2">"{t.text}"</p>
                <h4 className="font-semibold text-black">– {t.name}</h4>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 bg-gray-50">
        <div className="max-w-5xl mx-auto px-4">
          <h2 className="text-2xl font-bold text-center mb-6 font-playfair">
            {t("aboutUs")}
          </h2>
          <p className="text-gray-700 text-center max-w-2xl mx-auto mb-6">
            {t("aboutUsDescription")}
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 bg-white">
        <div className="max-w-xl mx-auto px-4">
          <h2 className="text-2xl font-bold text-center mb-8 font-playfair">
            {t("contactUs")}
          </h2>
          <form className="space-y-4">
            <input
              type="text"
              placeholder={t("yourName")}
              className="w-full border border-gray-300 rounded-md px-4 py-2"
            />
            <input
              type="email"
              placeholder={t("emailAddress")}
              className="w-full border border-gray-300 rounded-md px-4 py-2"
            />
            <textarea
              placeholder={t("yourMessage")}
              rows="5"
              className="w-full border border-gray-300 rounded-md px-4 py-2"
            ></textarea>
            <button
              type="submit"
              className="w-full custom-darkblue-button play-fair custom-shadow-effect"
            >
              {t("sendMessage")}
            </button>
          </form>
        </div>
      </section>

      {/* price calculator */}
      <div id="price-calculator" className="fixed bottom-10 right-24 z-50">
        <button
          id="calculator-button"
          onClick={() => setShowCalculator(!showCalculator)}
          className="w-24 h-24 rounded-full bg-black flex items-center justify-center shadow-2xl transition-colors duration-300 relative hover:animate-spin"
        >
          <svg
            viewBox="0 0 100 100"
            className="absolute inset-0 w-full h-full transition-transform duration-300 animate-spin-slow hover:[animation-play-state:paused] active:[animation-play-state:paused]"
          >
            <defs>
              <path
                id="circlePath"
                d="M50,50 m-45,0 a45,45 0 1,1 90,0 a45,45 0 1,1 -90,0"
              />
            </defs>
            <text
              fill="white"
              fontSize="30"
              dy="16"
              fontWeight="bold"
              className="transition-transform duration-300 drop-shadow-md"
            >
              <textPath href="#circlePath" startOffset="0%">
                {t("priceCalculator")}
              </textPath>
            </text>
          </svg>
        </button>

        {showCalculator && (
          <>
            <div className="fixed inset-0 bg-gray-200 bg-opacity-30 backdrop-blur-sm z-40">
              <div
                id="price-calculator-popup"
                className="fixed bottom-1/2 right-1/2 transform translate-x-1/2 translate-y-1/2 z-50 bg-gray-200 bg-opacity-80 backdrop-blur-md p-6 rounded-lg shadow-xl md:w-5/6 max-w-4xl max-h-[90vh] overflow-y-auto"
              >
                <PriceCalculator setShowCalculator={setShowCalculator} />
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Landing;
